import React from 'react';
import { Box, styled } from '@mui/material';
import { ColDef } from 'ag-grid-community';
import NumberFloatingFilter from 'ui/AgGridReact/NumberFloatingFilter';
import { DetailedDraftOperationT } from 'routes/BudgetModule/config/types';
import { useTranslation } from 'react-i18next';
import { TRACKABLE_EVENTS, trackEventBudget } from 'helpers/trackerHelpers';
import LinkCellRenderer from 'routes/BudgetModule/components/BudgetProposal/BudgetVentilated/components/LinkCellRenderer';
import {
    ALL_OPERATION_FILE_PREFIX,
    getExcelFilename,
} from 'routes/BudgetModule/contexts/helpers';
import AgGridReactExposingApi from 'routes/BudgetModule/components/shared/AgGridReactExposingAPI';
import DraftOperationDetailCellRenderer from './DraftOperationDetailCellRenderer';
import { DraftOperationActionsCellRenderer } from './DraftOperationActionsCellRenderer';
import TextFloatingFilter from 'ui/AgGridReact/TextFloatingFilter';
import { useFullScreen } from 'contexts/FullScreenContext';
import { useSelectSecondaryBudgetAxisSourceTable } from 'routes/BudgetModule/components/BudgetWithProvider/SecondaryBudgetAxisSourceTableContext';
import { translateBlock } from 'i18n';
import {
    BUDGET_EXCEL_EXPORT_PARAMS,
    BUDGET_EXCEL_STYLES,
} from 'routes/BudgetModule/config/excelExportHelpers';

const StyGrid = styled(Box, {
    name: 'StyGrid',
    shouldForwardProp: (prop) => prop !== 'isFullScreen',
})<{ isFullScreen: boolean }>(({ isFullScreen }) => ({
    width: '100%',
    height: isFullScreen ? '100%' : '40vh',
    minHeight: '300px',
    '&& .ag-root-wrapper': {
        borderBottomLeftRadius: '4px',
        borderBottomRightRadius: '4px',
    },
}));

export default function DraftOperationsGrid({
    draftOperations,
    draftOperationsColumns,
    exercice,
    expenseOrIncome,
}: {
    draftOperations: DetailedDraftOperationT[];
    draftOperationsColumns: ColDef[];
    exercice: number;
    expenseOrIncome: string;
}): React.ReactElement {
    const { t } = useTranslation();
    const { isFullScreen } = useFullScreen();
    const sourceTable = useSelectSecondaryBudgetAxisSourceTable();
    const colDefs: ColDef[] = draftOperationsColumns.map((column) => {
        const propsGrid = {
            floatingFilter: true,
            flex: 1,
            resizable: true,
            ...column,
            suppressFloatingFilterButton: true,
            menuTabs: [
                'filterMenuTab' as const,
                'generalMenuTab' as const,
                'columnsMenuTab' as const,
            ],
        };
        return { field: column.field, ...propsGrid };
    });

    return (
        <StyGrid className="ag-theme-material" isFullScreen={isFullScreen}>
            <AgGridReactExposingApi
                embedFullWidthRows
                detailRowAutoHeight
                rowHeight={36}
                headerHeight={40}
                rowGroupPanelShow="never"
                rowData={draftOperations}
                overlayNoRowsTemplate={t('common.noData')}
                columnDefs={colDefs}
                components={{
                    detailCellRenderer: DraftOperationDetailCellRenderer,
                    draftOperationActionsCellRenderer:
                        DraftOperationActionsCellRenderer,
                    numberFloatingFilter: NumberFloatingFilter,
                    textFloatingFilter: TextFloatingFilter,
                    linkCellRenderer: LinkCellRenderer,
                }}
                detailCellRenderer="detailCellRenderer"
                masterDetail
                onCellClicked={(cell) => {
                    if (cell.column.getColId() === 'actions') {
                        return;
                    }
                    cell.node.setExpanded(!cell.node.expanded);
                    trackEventBudget(
                        TRACKABLE_EVENTS.budget.budgetOverview.operations
                            .draftOperation.toggleDetails,
                        {
                            exercice,
                        },
                    );
                }}
                pagination
                paginationPageSize={10}
                excelExport={{
                    fileName: getExcelFilename(
                        `${ALL_OPERATION_FILE_PREFIX}_${sourceTable}`,
                        [expenseOrIncome],
                    ),
                    sheetName: t(
                        'budgetOverview.mantySecondaryBudgetAxisElement',
                        { sourceTable: translateBlock(sourceTable) },
                    ),
                }}
                excelStyles={BUDGET_EXCEL_STYLES}
                defaultExcelExportParams={BUDGET_EXCEL_EXPORT_PARAMS}
            />
        </StyGrid>
    );
}
